import type { ReceiverInput } from "@pdfking/functionpool"
import { CreatePDFSession } from "@pdfking/renderer-default"

import { ForcePDFReader } from "../../files/ForcePDFReader"
import { GenerateFile } from "../../files/GenerateFile"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import type { SinglePagePdf } from "../../types/files/cached/SinglePagePdf"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import type { TaskPage } from "../../types/tasks/input/TaskPage"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskMergeOptions } from "../../types/tasks/options/TaskMergeOptions"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

const findIndex = (arr: number[], num: number): number => {
  // use linear search up to at least 2 because binary search is bugged with 2 elements
  const l = arr.length
  if (l <= 8) {
    for (let i = 0; i < l; i++) {
      if (arr[i] > num) {
        return i
      }
    }
    return l
  }

  // binary search
  let left = 0
  let right = l - 1
  while (left <= right) {
    let mid = Math.floor((left + right) / 2)
    if (arr[mid] <= num) {
      left = mid + 1
    } else {
      right = mid - 1
    }
  }
  return left < l ? left : l
}

// options MergePdfOptions
// stream PdfDocument[1 // pagecount, ContentFile[]]
// emit TrackingCallback
//
// return PdfDocument[30, ContentFile[]]

export const TaskMerge = async function* (
  engine: PdfEngine,
  task: TaskMergeOptions,
  emit: TrackingCallback,
  messages: ReceiverInput<TaskPage>
): AsyncGenerator<TaskResult> {
  const result = await CreatePDFSession(async ({ reader, writer }) => {
    const emitter = (done: number, total: number) =>
      emit({
        name: TrackedTaskName.MERGE_PDF,
        done,
        total,
      })

    void emitter(0, 1)

    const page_registry: number[] = []
    const pages: SinglePagePdf[] = []

    // Create empty main document
    const main = await writer()

    void emitter(1, 1)

    let done = 0
    for await (const { page_number, page_count, content } of messages) {
      void emitter(done, page_count)

      const doc = await ForcePDFReader(engine, task.options, content, emit, {
        reader,
        writer,
      })
      const doc_page_count = await doc.page_count()

      // Validate that PDF has exactly 1 page like the original algorithm
      if (doc_page_count !== 1) {
        throw new Error(
          `PDF must have 1 page to be merged with this algorithm, it has ${doc_page_count} pages`
        )
      }

      pages.push(content as SinglePagePdf)

      const insertIndex = findIndex(page_registry, page_number)

      // Insert at the correct position (1-based for PDFium)
      // doc should already be a single-page PDF
      await main.insert(doc, insertIndex + 1)
      await doc.close()

      // Update our tracking array
      page_registry.splice(insertIndex, 0, page_number)

      done += 1
      void emitter(done, page_count)
    }

    // Sort collected pages by page_number
    pages.sort((a, b) => a.page_number - b.page_number)

    const content = await GenerateFile(engine, main.save(), ContentType.PDF)

    await main.close()

    return {
      enum: TaskName.CONTENT_FILE,
      name: "merge.pdf",
      content: {
        enum: VirtualFileType.MULTI_PAGE_PDF,
        content,
        pages,
        file_meta: {},
      },
    } as TaskResult
  })

  yield result
}
